import React, { createContext, ComponentType, useContext } from 'react';

import { withNaming } from '@bem-react/classname';
import { getBrowserStorage } from '@cryptoboyio/mercuryo-lab/utils';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import Resolution from '@unstoppabledomains/resolution';
import Cookies from 'js-cookie';

import {
  EnvsEnum,
  IntlDateType,
  TrimStringMiddleType,
  BemCSSModulesType,
  FingerPrintDataType,
  FullNameType,
  TestKeyType,
  CookieType,
  LocalStorageType,
  LanguageCodeType,
  SchemesEnum,
} from '@utils/types';

import { CurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';
import { EPaymentStatuses } from '@modules/transactions/types';

import currencySymbols from './currencySymbols';

export const resolution = new Resolution({ apiKey: CONST.apiKeyUD });

export const bemClassName = withNaming({ n: '', e: '__', m: '_', v: '_' });

export const regularValidationDefaultAddress = new RegExp(/^.{2,30}$/);

export const regularValidationStateCode = new RegExp(/^.{2,3}$/);

export const regularValidationZip = new RegExp(/^.{2,10}$/);

export const regularUnstoppableDomain = new RegExp(
  /^([a-zA-Z][a-zA-Z0-9-]{0,62}\.)+[a-zA-Z][a-zA-Z0-9-]{0,62}$/
);
export const emailReg =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const bemCssModules: BemCSSModulesType = (style, className) => {
  const bem: any = bemClassName(className);
  return (...args: any): string => {
    let mixins: Array<string> = [];
    const argsWithoutMixins = args.map((arg: any) => {
      if (Array.isArray(arg)) {
        mixins = mixins.concat(arg);
        return false;
      }
      return arg;
    });
    const bemResultWithoutMixins = bem(...argsWithoutMixins).split(' ');
    return bemResultWithoutMixins
      .map(cn => style[cn] || cn)
      .concat(mixins)
      .join(' ');
  };
};

export const trimStringMiddle: TrimStringMiddleType = (value, charCount) => {
  return value?.length > charCount
    ? [value.slice(0, Math.floor(charCount / 2)), value.slice(-Math.ceil(charCount / 2))].join('…')
    : value;
};

export const getFingerPrintData: FingerPrintDataType = () => {
  return new Promise(resolve => {
    const fpPromise = FingerprintJS.load();

    (async () => {
      const fp = await fpPromise;
      const result = await fp.get();
      const componentsObj = {};
      Object.keys(result.components).forEach(key => {
        const { value } = result.components[key];
        componentsObj[key] = value;
      });

      resolve({
        ...componentsObj,
        deviceId: result.visitorId,
      });
    })();
  });
};

export const getIntlDate: IntlDateType = (date, options, language = 'en') => {
  return new Intl.DateTimeFormat(language, options).format(new Date(date));
};

export const testKey: TestKeyType = key => {
  let testKeyFunction: (key?: string) => void;

  try {
    /* eslint-disable-next-line global-require */
    const WEBPACK_LOCAL_CONFIG = require('../../webpack-local.config');
    testKeyFunction = WEBPACK_LOCAL_CONFIG.testKey;
  } catch (e) {
    testKeyFunction = () => {};
  }
  testKeyFunction(key);
};

export const getFullName: FullNameType = (firstName, lastName) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return firstName || lastName || '';
};

export const handleLocalStorage = getBrowserStorage<LocalStorageType>('localStorage');

export const handleCookie: CookieType = {
  setCookie: (key, value, options) => Cookies.set(key, JSON.stringify(value), options),
  getCookie: key => JSON.parse(Cookies.get(key) || JSON.stringify('')),
  removeCookie: key => Cookies.remove(key),
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getPasswordValidationError = (password: string) => {
  if (password.length < 7) return 'validation.password_length';
  if (!/\d/.test(password)) return 'validation.password_number';
  if (!/[^A-Za-z0-9]/.test(password)) return 'validation.password_spec_char';
  return '';
};

export const getFormattedFiatCurrencyNumber = ({
  amount,
  currency,
  locale = 'en-US',
}: {
  amount: number;
  currency: FiatCurrencyEnum;
  locale?: LanguageCodeType;
}): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
  })
    .format(amount)
    .replace(currency, currencySymbols[currency])
    .replace(/^(\D+)/, '$1 ');

export const getCryptoDisplayDigits = (amount: string, displayDigits: number): string => {
  if (amount === '0') return amount;

  const [integerPart, fragPart] = amount.split('.');
  const slicedFragPart = fragPart?.slice(0, displayDigits);

  return [integerPart, slicedFragPart].join('.');
};

export const getPaymentStatusesForDisplay = (status: EPaymentStatuses): EPaymentStatuses => {
  switch (status) {
    case EPaymentStatuses.hold:
    case EPaymentStatuses.new:
    case EPaymentStatuses.order_scheduled:
      return EPaymentStatuses.pending;

    case EPaymentStatuses.failed:
    case EPaymentStatuses.order_failed:
    case EPaymentStatuses.descriptor_failed:
    case EPaymentStatuses.failed_payout:
    case EPaymentStatuses.failed_exchange:
    case EPaymentStatuses.limits_exceeded:
      return EPaymentStatuses.failed;

    case EPaymentStatuses.paid:
    case EPaymentStatuses.completed:
    case EPaymentStatuses.succeeded:
      return EPaymentStatuses.succeeded;

    case EPaymentStatuses.cancelled:
      return EPaymentStatuses.cancelled;

    default:
      return status;
  }
};

export const getDisplayStatus = (status: EPaymentStatuses): string =>
  getPaymentStatusesForDisplay(status).replace(/_/g, ' ');

export const isFiatCurrency = (currency: CurrencyEnum): currency is FiatCurrencyEnum =>
  currency in FiatCurrencyEnum;

export const getTestId =
  (name?: string) =>
  (subname?: string): string | undefined =>
    (CONST.env !== EnvsEnum.prod && (subname ? `${name}_${subname}` : name)) || undefined;

export const getRawCardNumber = (n: string): string => n.replace(/ /g, '');

export const currencyPath = (path: string): string => `${path}/:currency?`;

export const getStatus = (status?: EPaymentStatuses) => {
  if (!status) return { isFailed: false, isSucceeded: false };

  const isFailed = [
    EPaymentStatuses.failed,
    EPaymentStatuses.failed_exchange,
    EPaymentStatuses.failed_payout,
    EPaymentStatuses.cancelled,
    EPaymentStatuses.order_failed,
    EPaymentStatuses.failed_send,
    EPaymentStatuses.descriptor_failed,
    EPaymentStatuses.limits_exceeded,
  ].includes(status);

  const isSucceeded = [
    EPaymentStatuses.order_scheduled,
    EPaymentStatuses.paid,
    EPaymentStatuses.completed,
    EPaymentStatuses.succeeded,
    EPaymentStatuses.sending,
    EPaymentStatuses.sent,
    EPaymentStatuses.pending,
  ].includes(status);

  return { isFailed, isSucceeded };
};

export const getSystemColorScheme = (): SchemesEnum =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? SchemesEnum.dark
    : SchemesEnum.light;

export const isObject = (input: unknown): input is Record<string, unknown> => {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
};

export function createCustomContext<
  S extends Exclude<Record<string, unknown>, FunctionConstructor>
>(init: S | (() => S)) {
  const Context = createContext<S>({} as S);

  function withCustomProvider<T>(
    Wrapped: ComponentType<T>
  ): ComponentType<JSX.IntrinsicAttributes & T> {
    return props => {
      const value = typeof init === 'function' ? init() : init;
      return (
        <Context.Provider value={value}>
          <Wrapped {...props} />
        </Context.Provider>
      );
    };
  }

  const useCustomContext = (): S => useContext(Context);
  return { withCustomProvider, useCustomContext };
}

export const getCurrencySymbol = (currency: CurrencyEnum): string => {
  return currencySymbols?.[currency] ?? '';
};
